import { useQuery } from '@tanstack/react-query';
import { Building2, Database, ExternalLink, Key, Mail, Star } from 'lucide-react';
import { Card } from '../../../components/ui/card';
import { partnerApi } from '../../../lib/api';

interface ReputationMetricsData {
  tenantsWithReputationEnabled: number;
  tenantDetails: Array<{
    businessId: string;
    businessName: string;
    averageGoogleRating: number | null;
    totalGoogleReviews: number;
  }>;
}

export const ReputationMetrics = () => {
  const { data: metrics, isLoading } = useQuery<ReputationMetricsData>({
    queryKey: ['reputation-metrics'],
    queryFn: () => partnerApi.getReputationMetrics(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading metrics...</div>
      </div>
    );
  }

  if (!metrics) {
    const steps = [
      {
        title: 'Credentials & Frontend',
        description: 'Get your API credentials and embed the Reach UI into your platform',
        href: 'https://docs.embedreach.com/onboarding/credentials',
        icon: <Key className="h-4 w-4" />,
      },
      {
        title: 'Data Sync',
        description: 'Create schemas and sync customer and transaction data',
        href: 'https://docs.embedreach.com/onboarding/data-sync',
        icon: <Database className="h-4 w-4" />,
      },
      {
        title: 'Channel Setup',
        description: 'Configure email channels for sending review request emails',
        href: 'https://docs.embedreach.com/onboarding/channel-setup',
        icon: <Mail className="h-4 w-4" />,
      },
      {
        title: 'Reputation Config',
        description: 'Enable internal feedback and Google review management for tenants',
        href: 'https://docs.embedreach.com/onboarding/reputation',
        icon: <Star className="h-4 w-4" />,
      },
    ];

    return (
      <div className="space-y-6">
        <Card className="p-8">
          <div className="max-w-lg">
            <div className="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
              <Star className="h-5 w-5 text-gray-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Get started with Reputation</h3>
            <p className="text-sm text-gray-500 mb-6">
              Reputation automates review requests and feedback collection linked to Google Business
              Profiles. Complete these steps to start seeing metrics.
            </p>
            <div className="space-y-3">
              {steps.map((step, i) => (
                <a
                  key={step.title}
                  href={step.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors group">
                  <span className="text-gray-400 group-hover:text-gray-600 mt-0.5">{step.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                      <span className="text-gray-400 text-xs">{i + 1}.</span>
                      {step.title}
                      <ExternalLink className="h-3 w-3 text-gray-400" />
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Reputation Tenants</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {metrics.tenantsWithReputationEnabled}
              </p>
            </div>
            <div className="h-12 w-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <Building2 className="h-6 w-6 text-amber-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Tenant Details Table */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Tenants with Reputation Management</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Business Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Average Google Rating
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Google Reviews
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {metrics.tenantDetails.length > 0 ? (
                metrics.tenantDetails.map(tenant => (
                  <tr key={tenant.businessId} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {tenant.businessName}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      {tenant.averageGoogleRating !== null ? (
                        <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-500 mr-1 fill-yellow-500" />
                          <span className="text-gray-900 font-medium">
                            {tenant.averageGoogleRating.toFixed(1)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">No ratings</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {tenant.totalGoogleReviews}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-500">
                    No tenants with Reputation enabled yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
